import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  DURUM_SIRASI,
  donemKilitle,
  donemListesi,
  goruntulemeOrani,
  itirazPenceresiAc,
  kesinKapanis,
  kilitKontrolu,
  onKapanis,
  type DonemDurumu,
  type DonemOzeti,
  type KilitKontrolu,
} from '../api/donem';
import { Diyalog, GerekceDiyalogu } from '../bilesenler/Diyalog';
import { DonemDurumRozeti, tutarMetni } from '../bilesenler/DonemParcalari';
import { Tablo, type Sutun } from '../bilesenler/Tablo';
import { CokSatir, Dugme, HataKutusu, Sekme } from '../bilesenler/temel';
import { tarihMetni, zamanMetni } from '../bicim/tarih';
import { yetkiKullan, yetkilerHazirKullan } from '../yetki/yetki';
import { DonemItiraz } from './DonemItiraz';
import { DonemMutabakat } from './DonemMutabakat';

/**
 * DÖNEM KAPANIŞI.
 *
 * "DÖNEM KİLİTLENEBİLDİ Mİ" PİLOTUN EN SERT ÖLÇÜTÜDÜR; blokerlerin tamamı
 * orada patlar.
 *
 * AŞAMALAR SIRAYLA İLERLER, ATLANAMAZ, GERİYE ALINAMAZ. Panel yalnız SIRADAKİ
 * aşamanın düğmesini gösterir; hepsi birden gösterilseydi kullanıcı sıra
 * dışı bir aşamaya basar ve sunucudan anlaşılmaz bir hata alırdı.
 *
 * KİLİT GERİ ALINAMAZ — ayrı bir onay diyaloğu ve zorunlu gerekçe ister.
 */

type SekmeKodu = 'kapanis' | 'mutabakat' | 'itiraz';

export function Donem() {
  const yetkilerHazir = yetkilerHazirKullan();

  const donemGorebilir = yetkiKullan('PERSONEL_GORUNTULE', 'DONEM_KAPAT', 'RAPOR_GORUNTULE');
  const mutabakatGorebilir = yetkiKullan('MUTABAKAT_GIR', 'DONEM_KAPAT', 'RAPOR_GORUNTULE');
  const itirazGorebilir = yetkiKullan('ITIRAZ_KARAR', 'RAPOR_GORUNTULE');

  const [sekmeSecimi, sekmeSecimiYaz] = useState<SekmeKodu | null>(null);
  const sekme: SekmeKodu =
    sekmeSecimi ?? (donemGorebilir ? 'kapanis' : mutabakatGorebilir ? 'mutabakat' : 'itiraz');

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Dönem kapanışı</h1>
        <p className="mt-1 text-sm text-slate-600">
          Aşamalar sırayla ilerler: açık → ön kapanış → itiraz penceresi → kesin kapanış → kilitli.{' '}
          <strong>Kilitli dönem yeniden açılamaz.</strong>
        </p>
      </header>

      <div className="flex gap-1 border-b border-slate-200">
        {donemGorebilir && (
          <Sekme
            etkin={sekme === 'kapanis'}
            tikla={() => sekmeSecimiYaz('kapanis')}
            etiket="Dönemler"
          />
        )}
        {mutabakatGorebilir && (
          <Sekme
            etkin={sekme === 'mutabakat'}
            tikla={() => sekmeSecimiYaz('mutabakat')}
            etiket="Mutabakat"
          />
        )}
        {itirazGorebilir && (
          <Sekme
            etkin={sekme === 'itiraz'}
            tikla={() => sekmeSecimiYaz('itiraz')}
            etiket="İtirazlar"
          />
        )}
      </div>

      {!yetkilerHazir ? (
        <p className="text-sm text-slate-500">Yetkiler yükleniyor…</p>
      ) : sekme === 'kapanis' && donemGorebilir ? (
        <DonemListesi />
      ) : sekme === 'mutabakat' && mutabakatGorebilir ? (
        <DonemMutabakat />
      ) : itirazGorebilir ? (
        <DonemItiraz />
      ) : (
        <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          Bu ekranı görüntüleme yetkiniz yok.
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function DonemListesi() {
  const kapatabilir = yetkiKullan('DONEM_KAPAT');
  const [acik, acikYaz] = useState<DonemOzeti | null>(null);

  const donemler = useQuery({ queryKey: ['donem', 'liste'], queryFn: () => donemListesi() });

  const sutunlar: Sutun<DonemOzeti>[] = [
    {
      baslik: 'Dönem',
      hucre: (d) => (
        <span>
          <span className="block font-medium text-slate-900">{tarihMetni(d.donemAy)}</span>
          <span className="block text-xs text-slate-500">
            {tarihMetni(d.baslangic)} – {tarihMetni(d.bitis)}
          </span>
        </span>
      ),
    },
    { baslik: 'Durum', hucre: (d) => <DonemDurumRozeti durum={d.durum} />, daralt: true },
    {
      baslik: 'Personel',
      hucre: (d) => (d.toplamPersonel === null ? '—' : String(d.toplamPersonel)),
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: 'Net saat',
      // Saat toplamları METİN — sayıya çevrilmez.
      hucre: (d) => tutarMetni(d.toplamNetSaat),
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: 'Fazla mesai',
      hucre: (d) => tutarMetni(d.toplamFazlaMesaiSaat),
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: 'Brüt tutar',
      hucre: (d) => (
        <span className="whitespace-nowrap font-medium text-slate-900">
          {tutarMetni(d.toplamBrutMesaiTutar)}
        </span>
      ),
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: '',
      hucre: (d) => (
        <button
          type="button"
          onClick={() => acikYaz(d)}
          className="whitespace-nowrap rounded px-2 py-1 text-xs text-kurum-700 hover:bg-kurum-50"
        >
          {kapatabilir ? 'Yönet' : 'Ayrıntı'}
        </button>
      ),
      hizala: 'sag',
      daralt: true,
    },
  ];

  return (
    <div className="space-y-4">
      <HataKutusu hata={donemler.error} />

      <Tablo
        sutunlar={sutunlar}
        satirlar={donemler.data ?? []}
        anahtar={(d) => String(d.donemId)}
        bekliyor={donemler.isPending}
        bosMetin="Tanımlı dönem yok."
      />

      {acik !== null && (
        <DonemYonetimi donem={acik} kapatabilir={kapatabilir} kapat={() => acikYaz(null)} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

/** Sıradaki aşama ve ona götüren eylemin adı. */
const SONRAKI_ASAMA: Partial<
  Record<DonemDurumu, { etiket: string; hedef: DonemDurumu; uyari?: string }>
> = {
  acik: { etiket: 'Ön kapanışı yap', hedef: 'on_kapanis' },
  on_kapanis: { etiket: 'İtiraz penceresini aç', hedef: 'itiraz_penceresi' },
  itiraz_penceresi: {
    etiket: 'Kesin kapanışı yap',
    hedef: 'kesin_kapanis',
    uyari: 'Kesin kapanışı ÖN KAPANIŞI YAPANDAN BAŞKA biri yapmalıdır (görevler ayrılığı).',
  },
  kesin_kapanis: {
    etiket: 'Dönemi kilitle',
    hedef: 'kilitli',
    uyari: 'KİLİT GERİ ALINAMAZ. Kilitli dönem yeniden açılamaz.',
  },
};

function DonemYonetimi({
  donem,
  kapatabilir,
  kapat,
}: {
  donem: DonemOzeti;
  kapatabilir: boolean;
  kapat: () => void;
}) {
  const sorguIstemcisi = useQueryClient();

  const oranGorebilir = yetkiKullan('DONEM_KAPAT', 'RAPOR_GORUNTULE');

  const [aciklama, aciklamaYaz] = useState('');
  const [kilitAcik, kilitAcikYaz] = useState(false);

  /*
   * KİLİT KONTROLÜ YALNIZ `DONEM_KAPAT` İLE OKUNUR — liste ucu daha geniş
   * (`PERSONEL_GORUNTULE | RAPOR_GORUNTULE` de kabul ediliyor). Aynı koşula
   * bağlansaydı yalnız görüntüleme yetkisi olan kullanıcı 403 alırdı.
   */
  const kontrol = useQuery({
    queryKey: ['donem', 'kilit-kontrol', donem.donemId],
    queryFn: () => kilitKontrolu(donem.donemId),
    enabled: kapatabilir,
  });

  const oran = useQuery({
    queryKey: ['donem', 'goruntuleme-orani', donem.donemId],
    queryFn: () => goruntulemeOrani(donem.donemId),
    enabled: oranGorebilir,
  });

  const tazele = () => sorguIstemcisi.invalidateQueries({ queryKey: ['donem'] });

  const ilerlet = useMutation({
    // Dönüş tipi `unknown`: itiraz penceresi `{ bitis }` döndürüyor,
    // diğer iki aşama 204. Ortak imza için daraltılıyor.
    mutationFn: (hedef: DonemDurumu): Promise<unknown> => {
      const not = aciklama.trim() === '' ? undefined : aciklama.trim();

      switch (hedef) {
        case 'on_kapanis':
          return onKapanis(donem.donemId, not);
        case 'itiraz_penceresi':
          return itirazPenceresiAc(donem.donemId, not);
        case 'kesin_kapanis':
          return kesinKapanis(donem.donemId, not);
        default:
          // Kilit AYRI diyalogdan geçer; buraya düşmemeli.
          return Promise.reject(new Error('Kilit ayrı onaydan geçer.'));
      }
    },
    onSuccess: async () => {
      aciklamaYaz('');
      await tazele();
    },
  });

  const kilitle = useMutation({
    mutationFn: (girdi: { ikOnayi: boolean; gerekce: string }) =>
      donemKilitle(donem.donemId, girdi.ikOnayi, girdi.gerekce),
    onSuccess: async () => {
      kilitAcikYaz(false);
      await tazele();
      kapat();
    },
  });

  const sonraki = SONRAKI_ASAMA[donem.durum];
  const engeller = (kontrol.data ?? []).filter((k) => k.seviye === 'engel');
  const ikOnaylari = (kontrol.data ?? []).filter((k) => k.seviye === 'ik_onayi');

  return (
    <Diyalog
      baslik={`${tarihMetni(donem.donemAy)} dönemi`}
      aciklama={`${tarihMetni(donem.baslangic)} – ${tarihMetni(donem.bitis)}`}
      kapat={kapat}
      genis
      cocuk={
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <DonemDurumRozeti durum={donem.durum} />
            <AsamaCizgisi durum={donem.durum} />
          </div>

          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <Kutu etiket="Ön kapanış" deger={zamanMetni(donem.onKapanisZamani)} />
            <Kutu etiket="Kesin kapanış" deger={zamanMetni(donem.kesinKapanisZamani)} />
            <Kutu
              etiket="İtiraz penceresi"
              deger={
                donem.itirazBaslangic === null
                  ? '—'
                  : `${tarihMetni(donem.itirazBaslangic)} – ${tarihMetni(donem.itirazBitis)}`
              }
            />
            <Kutu etiket="Kilit" deger={zamanMetni(donem.kilitZamani)} />
          </dl>

          {oranGorebilir && oran.data && <GoruntulemeKutusu veri={oran.data} />}

          {kapatabilir && (
            <>
              <HataKutusu hata={kontrol.error} />
              <KilitKontrolListesi
                engeller={engeller}
                ikOnaylari={ikOnaylari}
                bekliyor={kontrol.isPending}
              />
            </>
          )}

          <HataKutusu hata={ilerlet.error} />

          {kapatabilir && sonraki !== undefined && (
            <section className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-4">
              <h3 className="text-sm font-semibold text-slate-800">Sıradaki aşama</h3>

              {sonraki.uyari !== undefined && (
                <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                  {sonraki.uyari}
                </p>
              )}

              {/* Kilit AYRI diyalogdan geçer; açıklama alanı ona ait değil. */}
              {sonraki.hedef !== 'kilitli' && (
                <CokSatir
                  etiket="Açıklama (isteğe bağlı)"
                  deger={aciklama}
                  degisti={aciklamaYaz}
                  satir={2}
                />
              )}

              <div className="flex justify-end">
                {sonraki.hedef === 'kilitli' ? (
                  <Dugme
                    tur="tehlike"
                    tikla={() => kilitAcikYaz(true)}
                    // ENGEL VARSA KİLİT DÜĞMESİ AÇILMAZ: `engel` seviyesi
                    // geçilemez ve sunucu zaten reddeder.
                    pasif={engeller.length > 0}
                    cocuk={sonraki.etiket}
                  />
                ) : (
                  <Dugme
                    tikla={() => ilerlet.mutate(sonraki.hedef)}
                    bekliyor={ilerlet.isPending}
                    cocuk={sonraki.etiket}
                  />
                )}
              </div>
            </section>
          )}

          {donem.durum === 'kilitli' && (
            <p className="rounded-md border border-slate-300 bg-slate-100 p-3 text-sm text-slate-700">
              Bu dönem kilitli. Kilit geri alınamaz; düzeltme yalnız sonraki dönemde yapılır.
            </p>
          )}

          <div className="flex justify-end">
            <Dugme tur="ikincil" tikla={kapat} cocuk="Kapat" />
          </div>

          {kilitAcik && (
            <GerekceDiyalogu
              baslik={`${tarihMetni(donem.donemAy)} dönemini kilitle`}
              aciklama="KİLİT GERİ ALINAMAZ. Gerekçe kalıcı kayıttır."
              etiket="Kilit gerekçesi"
              ipucu="Zorunlu — denetim izine yazılır."
              onayEtiketi="Kilitle"
              onayTuru="tehlike"
              // `KilitleDto.gerekce` @MaxLength(1000) — diğerlerinden geniş.
              enCokKarakter={1000}
              secenekEtiketi="270 saat aşımı onayı"
              secenekler={
                /*
                  İK ONAYI YALNIZ GEREKİYORSA SORULUR. 270 saat aşımı varsa
                  onaysız kilit, aşımın fark edilmeden bordroya girmesi
                  demektir (4857/41). Aşım yoksa soru anlamsız olurdu.
                */
                ikOnaylari.length > 0
                  ? [
                      {
                        kod: 'evet',
                        etiket: 'İK onayı verildi',
                        aciklama: ikOnaylari.map((k) => k.aciklama).join(' · '),
                      },
                    ]
                  : undefined
              }
              bekliyor={kilitle.isPending}
              hata={kilitle.error}
              kapat={() => kilitAcikYaz(false)}
              onayla={(gerekce, secenekKodu) =>
                kilitle.mutate({ ikOnayi: secenekKodu === 'evet', gerekce })
              }
            />
          )}
        </div>
      }
    />
  );
}

/* ------------------------------------------------------------------ */

function AsamaCizgisi({ durum }: { durum: DonemDurumu }) {
  const dizin = DURUM_SIRASI.indexOf(durum);

  return (
    <span className="flex items-center gap-1 text-xs text-slate-500">
      {DURUM_SIRASI.map((d, i) => (
        <span
          key={d}
          className={`h-1.5 w-6 rounded-full ${i <= dizin ? 'bg-kurum-500' : 'bg-slate-200'}`}
        />
      ))}
      <span className="ml-1">
        {dizin + 1}/{DURUM_SIRASI.length}
      </span>
    </span>
  );
}

/**
 * KİLİT ÖN KOŞULLARI.
 *
 * `engel` GEÇİLEMEZ · `ik_onayi` açık onayla geçilir. İkisi AYRI kutuda;
 * tek listede toplansaydı geçilemeyen bir engel "onayla ve devam et"
 * sanılırdı.
 */
function KilitKontrolListesi({
  engeller,
  ikOnaylari,
  bekliyor,
}: {
  engeller: KilitKontrolu[];
  ikOnaylari: KilitKontrolu[];
  bekliyor: boolean;
}) {
  if (bekliyor) return <p className="text-sm text-slate-500">Kilit koşulları kontrol ediliyor…</p>;

  if (engeller.length === 0 && ikOnaylari.length === 0) {
    return (
      <p className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
        Kilidi engelleyen koşul yok.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {engeller.length > 0 && (
        <section className="rounded-md border border-red-200 bg-red-50 p-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-red-800">
            Geçilemeyen engeller
          </h3>
          <ul className="mt-1 space-y-1 text-sm text-slate-800">
            {engeller.map((k) => (
              <li key={k.kod}>
                {k.aciklama} <span className="text-xs text-slate-500">({k.sayi} kayıt)</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {ikOnaylari.length > 0 && (
        <section className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-900">
            İK onayıyla geçilebilir
          </h3>
          <ul className="mt-1 space-y-1 text-sm text-slate-800">
            {ikOnaylari.map((k) => (
              <li key={k.kod}>
                {k.aciklama} <span className="text-xs text-slate-500">({k.sayi} kayıt)</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/**
 * GÖRÜNTÜLEME ORANI — PİLOT GÖSTERGESİ.
 *
 * Oran düşükse itiraz yokluğu "herkes memnun" DEĞİL "kimse bakmadı" demektir;
 * ikisi karıştırılırsa pilot yanlış okunur.
 */
function GoruntulemeKutusu({
  veri,
}: {
  veri: { toplamPersonel: number; goruntuleyen: number; oran: number };
}) {
  const dusuk = veri.oran < 50;

  return (
    <section
      className={`rounded-md border p-3 ${
        dusuk ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-white'
      }`}
    >
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
        Dönem özetini görenler
      </h3>
      <p className="mt-1 text-sm text-slate-800">
        {veri.goruntuleyen}/{veri.toplamPersonel} kişi · %{veri.oran}
      </p>
      {dusuk && (
        <p className="mt-1 text-xs text-amber-900">
          Oran düşük: itiraz gelmemesi “herkes memnun” değil “kimse bakmadı” anlamına gelebilir.
        </p>
      )}
    </section>
  );
}

function Kutu({ etiket, deger }: { etiket: string; deger: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <dt className="text-xs text-slate-500">{etiket}</dt>
      <dd className="mt-0.5 font-medium text-slate-900">{deger}</dd>
    </div>
  );
}
